import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { haptic } from "@/lib/haptic";
import { playNotificationSound } from "@/lib/notificationSound";
import usePushNotifications from "@/hooks/usePushNotifications";
import { useI18n } from "@/lib/i18n";
import { decodeMimeWords, senderLabel } from "@/lib/mime";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function hexToHSL(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function applyAccentColor(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return;
  const { h, s, l } = hexToHSL(hex);
  const root = document.documentElement.style;
  root.setProperty('--primary', `${h} ${s}% ${l}%`);
  root.setProperty('--ring', `${h} ${s}% ${l}%`);
}

export default function useEmailManager() {
  const { t } = useI18n();
  const [email, setEmail] = useState(null);
  const [messages, setMessages] = useState([]);
  // Miroir de `messages` pour le polling, qui doit lire la valeur courante
  // sans avoir à se recréer à chaque mise à jour de la liste.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [selectedLifetime, setSelectedLifetime] = useState("10min");
  const [currentLifetime, setCurrentLifetime] = useState("10min");

  // Push notifications
  const pushNotifs = usePushNotifications(email?.id);

  const [isRefreshingInbox, setIsRefreshingInbox] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isDeletingMessages, setIsDeletingMessages] = useState(false);

  // Notifications
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );

  const requestNotifications = useCallback(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().then((perm) => {
        setNotifPermission(perm);
        // Auto-subscribe to push when permission granted
        if (perm === "granted" && pushNotifs.isSupported && !pushNotifs.isSubscribed) {
          pushNotifs.subscribe();
        }
      });
    } else if (Notification.permission === "granted" && pushNotifs.isSupported && !pushNotifs.isSubscribed) {
      pushNotifs.subscribe();
    }
  }, [pushNotifs]);

  const notifyNewMessage = useCallback((msg) => {
    if (notifPermission !== "granted") return;
    if (document.visibilityState === "visible") return;
    try {
      new Notification(`ShadowMail - ${t.notifNewMessage}`, {
        body: t.notifFrom(senderLabel(msg), decodeMimeWords(msg.subject)),
        icon: "/icons/icon-192.png",
        badge: "/icons/icon-96.png",
        tag: msg.id,
      });
    } catch (e) {
      // SW notification fallback not needed for basic case
    }
    // Les traductions (t) changent à chaque bascule de langue : les inclure
    // recréerait ce callback et relancerait les effets qui en dépendent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifPermission]);

  // Site settings
  const [siteName, setSiteName] = useState("ShadowMail");
  const [allowPermanent, setAllowPermanent] = useState(false);
  const [allowCustomAddress, setAllowCustomAddress] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [accentColor, setAccentColor] = useState("#00f0ff");
  const [welcomeText, setWelcomeText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [customAddress, setCustomAddress] = useState("");
  const [customAddressError, setCustomAddressError] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [lifetimeOptions, setLifetimeOptions] = useState({
    "10min": { label: "10 minutes", seconds: 600 },
    "1h": { label: "1 heure", seconds: 3600 },
    "24h": { label: "24 heures", seconds: 86400 }
  });

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      const data = res.data;
      setSiteName(data.site_name);
      setAllowPermanent(data.allow_permanent || false);
      setAllowCustomAddress(data.allow_custom_address || false);
      setMaintenanceMode(data.maintenance_mode || false);
      setAccentColor(data.accent_color || "#00f0ff");
      setWelcomeText(data.welcome_text || "");
      setFooterText(data.footer_text || "");
      setDomains(data.domains || []);
      setSelectedDomain(data.domains?.[0] || "");
      const opts = {};
      (data.durations || []).filter(d => d.enabled !== false).forEach(d => {
        opts[d.key] = { label: d.label, seconds: d.minutes * 60 };
      });
      setLifetimeOptions(opts);
      setSelectedLifetime(data.default_duration || Object.keys(opts)[0]);
      // Apply accent color to CSS variables
      if (data.accent_color) {
        document.documentElement.style.setProperty('--accent-dynamic', data.accent_color);
        applyAccentColor(data.accent_color);
      }
    }).catch(() => {});
  }, []);

  // Address history (stored locally)
  const [addressHistory, setAddressHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('shadowmail_address_history') || '[]');
    } catch { return []; }
  });

  const addToAddressHistory = (emailData) => {
    if (!emailData?.full_address) return;
    setAddressHistory(prev => {
      const filtered = prev.filter(h => h.address !== emailData.full_address);
      const entry = {
        address: emailData.full_address,
        createdAt: new Date().toISOString(),
        lifetime: emailData.lifetime || "10min",
      };
      const updated = [entry, ...filtered].slice(0, 50);
      localStorage.setItem('shadowmail_address_history', JSON.stringify(updated));
      return updated;
    });
  };

  const clearAddressHistory = () => {
    setAddressHistory([]);
    localStorage.removeItem('shadowmail_address_history');
  };

  const saveEmailToStorage = (emailData) => {
    if (emailData) {
      localStorage.setItem('shadowmail_email_id', emailData.id);
      localStorage.setItem('shadowmail_email_data', JSON.stringify(emailData));
      addToAddressHistory(emailData);
    }
  };

  const clearEmailFromStorage = () => {
    localStorage.removeItem('shadowmail_email_id');
    localStorage.removeItem('shadowmail_email_data');
  };

  const restoreEmailFromStorage = useCallback(async () => {
    const savedEmailId = localStorage.getItem('shadowmail_email_id');
    if (!savedEmailId) return null;

    try {
      const response = await axios.get(`${API}/emails/${savedEmailId}`);
      const emailData = response.data;
      setEmail(emailData);
      setRemainingTime(emailData.remaining_seconds);
      setCurrentLifetime(emailData.lifetime || "10min");
      return emailData;
    } catch (error) {
      clearEmailFromStorage();
      return null;
    }
  }, []);

  const generateEmail = useCallback(async (lifetime = selectedLifetime) => {
    setIsLoading(true);
    setCustomAddressError("");
    const oldEmailId = email?.id;

    try {
      const payload = { lifetime };
      if (customAddress.trim()) {
        payload.custom_address = customAddress.trim();
      }
      if (selectedDomain) {
        payload.domain = selectedDomain;
      }
      // Pass user token if logged in to auto-link email to account
      const userToken = localStorage.getItem("user_token");
      const userExpires = localStorage.getItem("user_expires");
      const headers = {};
      if (userToken && userExpires && new Date(userExpires) > new Date()) {
        headers.Authorization = `Bearer ${userToken}`;
      }
      const response = await axios.post(`${API}/emails/generate`, payload, { headers });
      setEmail(response.data);
      setMessages([]);
      setRemainingTime(response.data.remaining_seconds);
      setCurrentLifetime(response.data.lifetime || lifetime);
      saveEmailToStorage(response.data);
      setCustomAddress("");
      setShowCustomInput(false);
      haptic("success");
      requestNotifications();
      toast.success(t.toastNewAddress, {
        description: `${response.data.full_address} (${lifetimeOptions[lifetime]?.label || lifetime})`,
      });

      // Only delete old email if user is NOT logged in (anonymous usage)
      // Logged-in users keep their old emails on their account
      const isLoggedIn = userToken && userExpires && new Date(userExpires) > new Date();
      if (oldEmailId && oldEmailId !== response.data.id && !isLoggedIn) {
        try {
          await axios.delete(`${API}/emails/${oldEmailId}`);
        } catch (e) {
          // Ignore deletion errors for old email
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        setCustomAddressError(detail || t.toastAddressInUse);
      } else if (error.response?.status === 400 || error.response?.status === 403) {
        setCustomAddressError(detail || t.toastInvalidAddress);
      } else {
        toast.error(t.toastGenerateError, {
          description: t.toastOverloaded,
        });
      }
    } finally {
      setIsLoading(false);
    }
    // Dépendances volontairement réduites : les helpers et traductions sont
    // recréés à chaque rendu, les inclure rendrait ce callback instable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLifetime, email?.id, customAddress, lifetimeOptions]);

  const fetchMessages = useCallback(async () => {
    if (!email?.id) return;
    try {
      const response = await axios.get(`${API}/emails/${email.id}/messages`);
      const data = response.data;
      // On lit la liste courante via une ref : le callback n'est recréé que
      // lorsque l'adresse change, une dépendance sur `messages` figerait la valeur.
      const prev = messagesRef.current;

      // Notify on new received messages (not sent)
      const newMsgs = data.filter(
        (m) => !prev.some((old) => old.id === m.id) && m.direction !== "sent"
      );
      if (newMsgs.length > 0 && prev.length > 0) {
        haptic("success");
        playNotificationSound();
        newMsgs.forEach((m) => notifyNewMessage(m));
      }

      // N'écrit dans l'état que si quelque chose a changé : sinon le polling
      // re-rendrait toute la liste toutes les 15 s pour rien.
      const changed =
        prev.length !== data.length ||
        data.some((m, i) => {
          const p = prev[i];
          return !p || p.id !== m.id || p.is_read !== m.is_read;
        });
      if (changed) setMessages(data);
    } catch (error) {
      if (error.response?.status === 410) {
        setEmail(null);
        setMessages([]);
        clearEmailFromStorage();
        toast.error(t.toastExpired, { description: t.toastExpiredDesc });
      }
    }
    // Ne dépend que de l'adresse : la liste courante est lue via messagesRef,
    // ce qui évite de recréer ce callback (et donc l'intervalle) à chaque
    // arrivée de message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email?.id]);

  const refreshInbox = useCallback(async () => {
    if (!email?.id || isRefreshingInbox) return;
    setIsRefreshingInbox(true);
    try {
      const response = await axios.get(`${API}/emails/${email.id}/messages`);
      setMessages(response.data);
      if (response.data.length > 0) {
        toast.success(t.toastInboxRefreshed, { description: t.messages(response.data.length), duration: 2000 });
      } else {
        toast.info(t.toastNoNewMessage, { duration: 2000 });
      }
    } catch (error) {
      if (error.response?.status === 410) {
        setEmail(null);
        setMessages([]);
        clearEmailFromStorage();
        toast.error(t.toastExpired);
      } else {
        toast.error(t.toastRefreshError);
      }
    } finally {
      setIsRefreshingInbox(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email?.id, isRefreshingInbox]);

  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev =>
      prev.includes(messageId)
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const selectAllMessages = () => {
    if (selectedMessages.length === messages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messages.map(m => m.id));
    }
  };

  const deleteSelectedMessages = async () => {
    if (selectedMessages.length === 0) return;
    setIsDeletingMessages(true);
    try {
      await axios.post(`${API}/messages/delete-bulk`, { message_ids: selectedMessages });
      haptic("medium");
      toast.success(t.toastDeleted(selectedMessages.length));
      setSelectedMessages([]);
      fetchMessages();
    } catch (error) {
      toast.error(t.toastDeleteError);
    } finally {
      setIsDeletingMessages(false);
    }
  };

  const markSelectedMessages = async (isRead) => {
    if (selectedMessages.length === 0) return;
    try {
      await axios.post(`${API}/messages/mark-read`, { message_ids: selectedMessages, is_read: isRead });
      haptic("light");
      toast.success(isRead ? t.toastMarkedRead : t.toastMarkedUnread);
      setSelectedMessages([]);
      fetchMessages();
    } catch (error) {
      toast.error(t.toastUpdateError);
    }
  };

  const fetchMessage = async (messageId) => {
    try {
      const response = await axios.get(`${API}/messages/${messageId}`);
      setSelectedMessage(response.data);
      setIsModalOpen(true);
      fetchMessages();
    } catch (error) {
      toast.error(t.toastLoadError);
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.post(`${API}/messages/delete-bulk`, { message_ids: [messageId] });
      haptic("medium");
      toast.success(t.toastDeleted(1));
      setIsModalOpen(false);
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error(t.toastDeleteError);
    }
  };

  const copyToClipboard = async () => {
    if (!email?.full_address) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(email.full_address);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = email.full_address;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      haptic("light");
      toast.success(t.toastCopied, { description: t.toastCopiedDesc });
    } catch (error) {
      toast.error(t.toastCopyError);
    }
  };

  const deleteEmail = async () => {
    if (!email?.id) return;
    try {
      await axios.delete(`${API}/emails/${email.id}`);
      setEmail(null);
      setMessages([]);
      clearEmailFromStorage();
      haptic("medium");
      toast.success(t.toastEmailDeleted);
    } catch (error) {
      toast.error(t.toastDeleteError);
    }
  };

  // Initialize email on mount - only restore from storage, don't auto-generate
  useEffect(() => {
    let mounted = true;
    const initEmail = async () => {
      setIsLoading(true);
      await restoreEmailFromStorage();
      if (mounted) {
        setIsLoading(false);
        requestNotifications();
      }
    };
    initEmail();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for messages every 15 seconds
  useEffect(() => {
    if (!email?.id) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [email?.id, fetchMessages]);

  // Countdown timer
  useEffect(() => {
    if (!email) return;
    if (email.lifetime === "permanent" || remainingTime === -1) return;
    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          setEmail(null);
          setMessages([]);
          clearEmailFromStorage();
          toast.error(t.toastExpired, { description: t.toastExpiredDesc });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email?.id, email?.lifetime]);

  const formatTime = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(t.dateLocale, { hour: "2-digit", minute: "2-digit" });
  };

  const isPermanent = email?.lifetime === "permanent" || remainingTime === -1;
  const progressValue = isPermanent ? 100 : (remainingTime / (lifetimeOptions[currentLifetime]?.seconds || 600)) * 100;
  const isCritical = !isPermanent && remainingTime < 60;

  // Dynamic page title with unread count
  const unreadCount = messages.filter(m => !m.is_read && m.direction !== "sent").length;
  useEffect(() => {
    const baseTitle = t.pageTitle(siteName);
    document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount, siteName]);

  return {
    email, messages, selectedMessage, isLoading, isModalOpen, setIsModalOpen,
    remainingTime, selectedLifetime, setSelectedLifetime, currentLifetime,
    isRefreshingInbox, selectedMessages, isDeletingMessages,
    siteName, allowPermanent, allowCustomAddress, maintenanceMode,
    customAddress, setCustomAddress, customAddressError, setCustomAddressError,
    showCustomInput, setShowCustomInput, lifetimeOptions,
    generateEmail, fetchMessages, refreshInbox,
    toggleMessageSelection, selectAllMessages, deleteSelectedMessages, markSelectedMessages,
    fetchMessage, deleteMessage, copyToClipboard, deleteEmail,
    formatTime, formatDate,
    isPermanent, progressValue, isCritical,
    notifPermission, requestNotifications,
    pushNotifs,
    accentColor, welcomeText, footerText,
    domains, selectedDomain, setSelectedDomain,
    addressHistory, clearAddressHistory,
  };
}
